import { useEffect, useMemo, useState } from 'react'
import request from '../../../../api'
import {
    CoreConfigParent,
    CoreConfigResponse,
} from '../../../../interfaces/examiner/CoreConfig'
import {
    buildInitialExpandedGroups,
    buildInitialExpandedParents,
    collectValues,
    findSection,
    updateParentsField,
} from './helpers'

export function useCoreConfigEditor() {
    const [parents, setParents] = useState<CoreConfigParent[]>([])
    const [activeSectionId, setActiveSectionId] = useState('')
    const [expandedParents, setExpandedParents] = useState<Record<string, boolean>>({})
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [resetting, setResetting] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')

    const activeSection = useMemo(
      () => findSection(parents, activeSectionId),
      [parents, activeSectionId]
    )

    useEffect(() => {
        loadConfig()
    }, [])

    async function loadConfig() {
        setLoading(true)
        setError('')
        try {
            const res = await request('/api/examiner/core-config')
            if (!res.ok) {
                setError(await res.text())
                return
            }
            const data: CoreConfigResponse = await res.json()
            setParents(data.parents)
            const firstParent = data.parents[0]
            const firstSection = firstParent?.sections[0]
            if (firstParent && firstSection) {
                setActiveSectionId(firstSection.id)
                setExpandedParents(buildInitialExpandedParents(data.parents))
                setExpandedGroups(buildInitialExpandedGroups(data.parents))
            }
        } catch {
            setError('Could not load configurations')
        } finally {
            setLoading(false)
        }
    }

    function updateFieldValue(key: string, value: string) {
        setParents((prev) => updateParentsField(prev, key, value))
        setSuccess('')
    }

    function toggleParent(parentId: string) {
        setExpandedParents((prev) => ({
            ...prev,
            [parentId]: !prev[parentId],
        }))
    }

    function toggleGroup(groupId: string) {
        setExpandedGroups((prev) => ({
            ...prev,
            [groupId]: !prev[groupId],
        }))
    }

    function selectSection(sectionId: string, parentId: string) {
        setExpandedParents((prev) => ({
            ...prev,
            [parentId]: true,
        }))
        setActiveSectionId(sectionId)
        setSuccess('')
        setError('')
    }

    async function saveConfig() {
        const values = collectValues(parents)
        const dateFrom = values['email_teachers/teachers_email_verification/date_from']
        if (!dateFrom || !dateFrom.trim()) {
            setError('Active from is required')
            setSuccess('')
            return
        }

        const blockAfterDays = values['email_teachers/teachers_email_verification/block_after_days']
        if (!blockAfterDays || !/^\d+$/.test(blockAfterDays.trim()) || parseInt(blockAfterDays, 10) < 1) {
            setError('Auto-block after (days) is required and must be at least 1')
            setSuccess('')
            return
        }

        setSaving(true)
        setError('')
        setSuccess('')

        try {
            const res = await request('/api/examiner/core-config', {
                method: 'PUT',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ values }),
            })
            if (!res.ok) {
                setError(await res.text())
                return
            }
            const data: CoreConfigResponse = await res.json()
            setParents(data.parents)
            setSuccess('Configuration saved successfully')
        } catch {
            setError('Could not save configurations')
        } finally {
            setSaving(false)
        }
    }

    async function runFieldAction(key: string) {
        setResetting(true)
        setError('')
        setSuccess('')

        try {
            if (key === 'email_teachers/teachers_email_verification/force_reset') {
                const res = await request(
                  '/api/examiner/core-config/force-reset-teacher-email-verification',
                  { method: 'POST' }
                )
                if (!res.ok) {
                    setError(await res.text())
                    return
                }
                const data: { forceResetCount?: number } = await res.json()
                const count = typeof data.forceResetCount === 'number' ? data.forceResetCount : 0
                setSuccess(`Cleared email verification for ${count} active teacher(s).`)
                return
            }

            if (key === 'email_teachers/teachers_email_verification/block_after_days_run_now') {
                const res = await request(
                  '/api/examiner/core-config/run-block-unverified-teachers',
                  { method: 'POST' }
                )
                if (!res.ok) {
                    setError(await res.text())
                    return
                }
                const data: CoreConfigResponse & {
                    blocked?: number
                    skipped?: boolean
                    reason?: string
                } = await res.json()
                if (data.parents) {
                    setParents(data.parents)
                }
                if (data.skipped) {
                    setSuccess(
                      data.reason === 'auto_block_disabled'
                        ? 'Auto-block job ran but skipped: Auto-block enabled is off.'
                        : data.reason === 'block_after_days_invalid'
                          ? 'Auto-block job ran but skipped: Auto-block after (days) is invalid.'
                          : 'Auto-block job ran but skipped.'
                    )
                } else {
                    const blocked = typeof data.blocked === 'number' ? data.blocked : 0
                    setSuccess(`Auto-block job finished. Blocked ${blocked} teacher(s).`)
                }
                return
            }
        } catch {
            setError('Could not run configuration action')
        } finally {
            setResetting(false)
        }
    }

    return {
        parents,
        activeSection,
        activeSectionId,
        expandedParents,
        expandedGroups,
        loading,
        saving,
        resetting,
        error,
        success,
        updateFieldValue,
        toggleParent,
        toggleGroup,
        selectSection,
        saveConfig,
        runFieldAction,
    }
}
